import simple_jsonrpc from 'simple-jsonrpc-js'


export class WebSocketConnectionManager {
	constructor() {
        this.ws_path = null
		this.websocket = null
		this.reconnectTimer = null
		this.attempts = 0

        this._onDisconnect = new Event()
		this._onConnect = new Event()
	}

	onConnect(cb) {
        return this._onConnect.subscribe(cb)
	}

	onDisconnect(cb) {
        return this._onDisconnect.subscribe(cb)
	}

	closeHandler = (evt) => {
		if (evt.target == this.websocket) {
			this.websocket = null
			let results = this._onDisconnect.fire(evt.code, evt.reason, evt.target)

			if (results.indexOf(false) === -1) {
				this.reconnect()
			}
		}
	}

	connectedHandler = (evt) => {
		if (evt.target == this.websocket) {
            this._onConnect.fire(evt.target)

            // Reset number of attempts if the socket remains open for > 5 seconds
            setTimeout(() => {
                if (this.websocket == evt.target)
                    this.attempts = 0
            }, 5000);

		} else {
			evt.target.close(1000)
		}
	}

	reconnect() {
		this.attempts += 1
		if (this.attempts == 1) {
			this.internalConnect()
		} else {
			let backoff = Math.pow(2, this.attempts - 1)
			if (backoff > 30)
				backoff = 30
			this.reconnectTimer = setTimeout(this.internalConnect, backoff * 1000)
		}
	}

	connect (path) {
		this.attempts = 0
		this.ws_path = path
		if (this.websocket) {
			// let the close event call reconnect.
			this.websocket.close(1000)
		} else {
			this.internalConnect()
		}
	}

	internalConnect = () => {
		if (this.reconnectTimer !== null) {
			clearTimeout(this.reconnectTimer)
			this.reconnectTimer = null
		}

		if (this.ws_path) {
			const proto = (window.location.protocol == 'https:') ? 'wss:' : 'ws:'
			const websocket_url = proto + '//' + window.location.host + this.ws_path

            this.websocket = new WebSocket(websocket_url)

            // onclose should always be called even after onerror, so not
            // bothering handling that since they would do the same thing (for now anyway)
			this.websocket.onclose = this.closeHandler
			this.websocket.onopen = this.connectedHandler
		}
    }
}

export class ApiConnection {
	constructor() {
        this.manager = new WebSocketConnectionManager()
        this.rpc = new simple_jsonrpc()
        this.queue = []
		this._onDisconnect = new Event()
		this._onConnect = new Event()
        this.websocket = null

        this.rpc.toStream = (_msg) => {
            if (this.websocket !== null)
                this.websocket.send(_msg)
            else
                this.queue.push(_msg)
        }

        this.manager.onConnect((ws) => {
            this.websocket = ws
            ws.onmessage = (evt) => {
                this.rpc.messageHandler(evt.data)
            }

            for (let m of this.queue) {
                ws.send(m)
            }
            this.queue = []

            this._onConnect.fire()
        });

        this.manager.onDisconnect(() => {
            this.websocket = null
            this._onDisconnect.fire()
        });

        this.call = this.rpc.call
        this.on = this.rpc.on
        this.off = this.rpc.off

        this.manager.connect('/api/rpc')
	}

    get connected() {
        return this.websocket !== null
    }

	onConnect(cb) {
        return this._onConnect.subscribe(cb)
	}

	onDisconnect(cb) {
        return this._onDisconnect.subscribe(cb)
	}
}

export class ApiStateManager {
	constructor(jsonrpc) {
		this.jsonrpc = jsonrpc
		this.queries = new Map()
		this.subs = new Map()
		this.subscriber_map = new Map()
        this.valid = true
        this.jsonrpc.onDisconnect(this.invalidate)
        this.jsonrpc.onConnect(this.validate)
	}

    invalidate = () => {
        this.valid = false
    }

    validate = () => {
        this.subscriber_map = new Map()
        this.subs = new Map()

        for (let cls_collection of this.queries.values()) {
            for (let query of cls_collection.values())
                this.register(cls_collection, query)
        }
    }

	query(cls, ...args) {
		const collection = cls;
		const key = cls.key(...args)
		let cls_collection = this.queries.get(cls)

		if (cls_collection === undefined) {
			cls_collection = new Map()
			this.queries.set(collection, cls_collection)
		}

		let query = cls_collection.get(key)

		if (query === undefined) {
			query = new cls(this.jsonrpc, ...args)
            this.register(cls_collection, query)
		}

		return query
	}

    register = (collection, query) => {
        collection.set(query.query_key, query)
        this.register_subscriptions(query)

        // start the initial fetch
        query.fetch()

    }

	register_subscriptions(obj) {
		const subs = obj.subscriptions()
		if (subs) {
			for (const sub of obj.subscriptions()) {
				this.register_subscription(obj, sub)
			}
		}
	}

	register_subscription(obj, sub) {
		const notification = this.get_notification_method(sub)
		const subscription = {
			'topic': sub['topic'],
			'event': sub['event'],
		}

		if (sub['match']) {
			Object.assign(subscription, sub['match'])
		}

		let notification_subs = this.subs.get(notification)
		if (notification_subs === undefined) {
			notification_subs = new Map()
			this.subs.set(notification, notification_subs)
			this.jsonrpc.on(notification, "pass", (data) => {this.notify(notification, data)})
		}

		// Assumption: every object has only one set of match params for a given notification
		notification_subs.set(obj, sub['match'])

		if (this.subscriber_map.get(obj) === undefined)
			this.subscriber_map.set(obj, [])

		const sub_desc = {
			notification: notification,
			unsubscribe: null,
		}
		this.subscriber_map.get(obj).push(sub_desc)

		this.jsonrpc.call('api.v2.events.subscribe', subscription).then((result) => {
			// TODO: should this just reutrn a uuid??? (yah, probably)
			// subscription args so we can unsubscribe later.
			sub_desc.unsubscribe = result
		});
	}

	notify = (notification, data) => {
		const subs = this.subs.get(notification)
		if (subs) {
			for (let [obj, match] of subs.entries()) {
				if (this.match_notification(match, data))
					this.dispatch(notification, data, obj) 
			}
		}
	}

	match_notification(match, data) {
		if (!match) {
			return true
		} else {
			for (const [k, v] of Object.entries(match)) {
				if (data[k] != v)
					return false
			}
			return true
		}
	}

	get_notification_method(sub) {
		return sub['topic'] + '.' + sub['event']
	}

	dispatch(notification, data, obj) {
		obj.notify(notification, data)
	}

	unregister = (obj) => {
		const subs = this.subscriber_map.get(obj)
		if (subs !== undefined) {
			this.subscriber_map.delete(obj)
			for (const sub of subs) {
				this.unregister_subscription(obj, sub)
			}
		}
	}

	unregister_subscription(obj, sub) {
		if (this.subs.has(sub.notification)) {
			const notification_subs = this.subs.get(sub.notification)
			notification_subs.delete(obj)

			if (notification_subs.size === 0) {
				this.subs.delete(sub.notification)
				this.jsonrpc.off(sub.notification)
			}
		}

		if (this.queries.has(obj.constructor)) {
			const cls_queries = this.queries.get(obj.constructor)
			cls_queries.delete(obj.query_key)

			if (cls_queries.size === 0) {
				this.queries.delete(obj.constructor)
			}
		}

		this.jsonrpc.call('api.v2.events.unsubscribe', sub.unsubscribe)
	}
}

class Event {
    constructor() {
		this.callbacks = []
    }

    subscribe = (cb) => {
        this.callbacks.push(cb)
    }

    fire = (...args) => {
        let results = []
        for (let cb of this.callbacks)
            results.push(this.fire_cb(cb, ...args))

        return results
    }

	fire_cb(cb, ...args) {
		try {
			return cb(...args)
		} catch (e) {
			console.log(e)
		}
	}
}

class DynamicResult {

	constructor() {
		this.callbacks = []
		this.populated = false
		this.result = null
	}

	set_value = (result) => {
		this.populated = true
		this.result = result
		for (const cb of this.callbacks) 
			this.fire(cb)
	}

	get value () {
		return this.result;
	}

	fire(cb) {
		try {
			cb(this.result)	
		} catch (e) {
			console.log(e)
		}
	}

	updated = (cb) => {
		this.callbacks.push(cb)
		if (this.populated === true)
			this.fire(cb)
	}
}

export class VueComponentApiProxy {
	constructor(component, api) {
		this.api = api
        this.jsonrpc = api.jsonrpc
		this.queries = []
		component.$once('hook:beforeDestroy', this.unregister)
	}

	query = (...args) => {
		const query = this.api.query(...args)
		this.queries.push(query)
		return query
	}

	unregister = () => {
		for (const query of this.queries) {
			this.api.unregister(query)
		}
	}

    attach = (component) => {
        return this.constructor(component, this.api)
    }
}

class ApiQuery {
	constructor(jsonrpc, ...args) {
        this.jsonrpc = jsonrpc
		this.result = new DynamicResult()
		this.updated = this.result.updated
		this.query_args = args
		let argmap = this.argmap()
        this.valid = true

		if (argmap) {
			this.args = new Map()
			for (let i = 0; i < args.length; ++i)
				this.args[argmap[i]] = args[i]

		} else {
			this.args = args
		}
	}

	get query_key() {
		return this.constructor.key(...this.query_args)
	}

	update_result(result) {
		this.result.set_value(result)
	}

	subscriptions() {}
	argmap() {} 
	notify = () => {}
	fetch() {}

	static key(...args) {
		return args.join('-')
	}
}

export class VersionInfo extends ApiQuery {
	fetch = () => {
       this.jsonrpc.call('api.v2.version.get').then((result) => {
           this.update_result(result)
       })
    }
}

export class ClientConnections extends ApiQuery {

	argmap() {
		return ['server_id', 'client_id']
	}

	subscriptions = () => {
		const subs = []
		for (const evt of ['opened', 'closed']) {
			subs.push({
				'topic': 'api.v2.connection', 
				'event': evt,
				'match': {
					'client_id': this.args['client_id'],
					'server_id': this.args['server_id'],
				}
			})
		}

		return subs
	}

	notify = (notification, data) => {
		const connections = [];

		if (notification == 'api.v2.connection.closed') {
			for (let conn of this.result.value) {
				if (conn.id != data.id) {
					connections.push(conn);
				}
			}

		} else if (notification == 'api.v2.connection.opened') {
			connections.push(...this.result.value);
			connections.push(data);
		}

		this.update_result(connections);
	}

	fetch = () => {
		this.jsonrpc.call('api.v2.servers.get_client_connections', this.args).then((result) => {
			this.update_result(result)
		});
	}
}

export class IotServers extends ApiQuery {

	subscriptions = () => {
		return [
			{
				'topic': 'api.v2.server',
				'event': 'started',
			},
			{
				'topic': 'api.v2.server',
				'event': 'stopped',
			},
		];
	}

	notify = (notification, data) => {
		const servers = [];

		if (notification == 'api.v2.server.stopped') {
			for (let server of this.result.value) {
				if (server.id != data.id) {
					servers.push(server);
				}
			}

		} else if (notification == 'api.v2.server.started') {
			servers.push(...this.result.value);
			servers.push(data);
		}

		this.update_result(servers);
	}

	fetch = () => {
		this.jsonrpc.call('api.v2.servers.get').then((result) => {
			this.update_result(result);
       })
    }
}

export class IotServerConnections extends ApiQuery {
	subscriptions = () => {
		return [
			{
				'topic': 'api.v2.connection',
				'event': 'opened',
				'match': {
					'server_id': this.args['id'],
				},
			},
			{
				'topic': 'api.v2.connection',
				'event': 'closed',
				'match': {
					'server_id': this.args['id'],
				},
			},
		];
	}

	argmap() {
		return ['id']
	}

	notify = (notification, data) => {
		const connections = [];

		if (notification == 'api.v2.connection.closed') {
			for (let conn of this.result.value) {
				if (conn.id != data.id) {
					connections.push(conn);
				}
			}

		} else if (notification == 'api.v2.connection.opened') {
			connections.push(...this.result.value);
			connections.push(data);
		}

		this.update_result(connections);
	}

	fetch = () => {
		this.jsonrpc.call('api.v2.servers.get_connections', {'server_id': this.args['id']}).then((result) => {
			this.update_result(result);
       })
    }
}
